/**
 * Subscription Management Dashboard
 * Displays current subscription and management options
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { MemberTeamsSection } from '@/components/feature/MemberTeamsSection';
import { SubscriptionDashboard } from '@/components/feature/SubscriptionDashboard';
import { UsageLimit } from '@/components/feature/UsageLimit';

export function SubscriptionDashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setCurrentSubscription, invalidate } = useSubscriptionStore();
  const {
    currentSubscription,
    usageLimit,
    loading,
    error,
    loadCurrentSubscription,
    loadUsageLimit,
    upgradePlan,
    downgradePlan,
    cancelSubscription,
    clearError,
  } = useSubscription();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?mode=login');
      return;
    }

    loadCurrentSubscription();
    loadUsageLimit();
  }, [isAuthenticated, loadCurrentSubscription, loadUsageLimit, navigate]);

  // Sync to global store so Navbar badge stays up to date
  useEffect(() => {
    if (currentSubscription) {
      setCurrentSubscription(currentSubscription);
    }
  }, [currentSubscription, setCurrentSubscription]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F]">
        <Navbar />
        <div className="pt-32 pb-20 px-6 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/50">Carregando sua assinatura...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentSubscription) {
    return (
      <div className="min-h-screen bg-[#0A0A0F]">
        <Navbar />
        <div className="pt-32 pb-20 px-6 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/50 mb-6">Você não tem uma assinatura ativa</p>
            <button
              onClick={() => navigate('/checkout')}
              className="py-3 px-6 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882] transition-all"
            >
              Criar Assinatura
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />

      <div className="pt-32 pb-20 px-6 md:px-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h1
              className="text-5xl font-light text-white mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Sua <strong>Assinatura</strong>
            </h1>
            <p className="text-white/40">Gerencie seu plano e visualize seu uso</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-8">
              <p className="text-sm text-red-400">{error}</p>
              <button
                onClick={clearError}
                className="text-xs text-red-400 hover:text-red-300 mt-2"
              >
                Descartar
              </button>
            </div>
          )}

          {/* Subscription Dashboard */}
          <SubscriptionDashboard
            subscription={currentSubscription}
            onUpgrade={() => {
              navigate('/checkout?mode=upgrade');
            }}
            onDowngrade={() => {
              navigate('/checkout?mode=downgrade');
            }}
            onCancel={async () => {
              try {
                await cancelSubscription();
                invalidate();
                window.location.reload();
              } catch (err) {
                console.error('Error cancelling subscription:', err);
              }
            }}
            isLoading={loading}
          />

          {/* Usage Limit - if has active subscription */}
          {currentSubscription.status === 'active' && usageLimit && (
            <div className="mt-10">
              <h2 className="text-2xl font-semibold text-white mb-6">Seu Uso</h2>
              <UsageLimit usage={usageLimit} />

              {/* Usage Info */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-400">
                  💡 Ao atingir 80% do limite, você receberá um aviso. Para usar além do limite, considere fazer upgrade de plano.
                </p>
              </div>
            </div>
          )}

          {/* Teams membership info */}
          <MemberTeamsSection />

          {/* Related Actions */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="/checkout"
              className="block p-4 bg-[#111111] border border-white/5 rounded-lg hover:border-[#C8A96E]/30 transition-all"
            >
              <p className="text-white font-semibold">📊 Ver Todos os Planos</p>
              <p className="text-white/40 text-sm mt-1">Compare e mude seu plano quando precisar</p>
            </a>
            <a
              href="/billing-history"
              className="block p-4 bg-[#111111] border border-white/5 rounded-lg hover:border-[#C8A96E]/30 transition-all"
            >
              <p className="text-white font-semibold">📜 Histórico de Faturamento</p>
              <p className="text-white/40 text-sm mt-1">Visualize suas faturas e recibos</p>
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
